import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  AcademicCapIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BellIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClockIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  FolderIcon,
  HomeIcon,
  BanknotesIcon,
  PencilSquareIcon,
  RectangleGroupIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { getSchoolProfile } from '../api/schoolApi';

import { PageLoader } from './Loader';
import ProfileDrawer from './ProfileDrawer';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://api.pawaschool.net/api/v1';

const STATIC_BASE_URL = API_BASE_URL.replace(/\/api\/v1$/, '');

const ADMIN_ROLES = ['admin', 'school_admin', 'super_admin'];

const getResponseArray = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(payload)) return payload;
  return [
    payload.rows,
    payload.students,
    payload.teachers,
    payload.children,
    response?.data?.rows,
  ].find(Array.isArray) || [];
};

const getRecordedAssessmentKeys = (term) =>
  ['test1', 'test2', 'exam'].filter((key) => {
    const rawScore = term?.[key];
    const score = Number(rawScore);
    return rawScore !== null && rawScore !== undefined && rawScore !== '' &&
      Number.isFinite(score) && score > 0;
  });

// Return the same stable activity IDs used by StudentNotifications.jsx.
// This lets the top-navigation badge share the page's read/unread state.
const fetchNotificationActivityIds = async (user, role) => {
  if (!user?.id) return [];

  if (role === 'teacher') {
    const teacherResponse = await apiClient.get('/teachers/me');
    const teacher = teacherResponse?.data?.data;
    if (!teacher?.id) return [];

    const [classesResult, subjectsResult] = await Promise.allSettled([
      apiClient.get(`/teachers/${teacher.id}/classes`),
      apiClient.get(`/teachers/${teacher.id}/subjects`),
    ]);
    const classes = classesResult.status === 'fulfilled'
      ? getResponseArray(classesResult.value) : [];
    const subjects = subjectsResult.status === 'fulfilled'
      ? getResponseArray(subjectsResult.value) : [];
    const ids = [
      ...classes.map((item, index) => `teacher-class-${item.id || index}`),
      ...subjects.map((assignment, index) => {
        const subject = assignment.Subject || assignment.subject || {};
        const assignedClass = assignment.Class || assignment.class || {};
        return `teacher-subject-${assignment.id || `${subject.id || index}-${assignedClass.id || 'class'}`}`;
      }),
    ];
    if (teacher.created_at || teacher.createdAt) {
      ids.push(`teacher-joined-${teacher.id}`);
    }
    return ids;
  }

  if (ADMIN_ROLES.includes(role)) {
    const [studentsResult, teachersResult] = await Promise.allSettled([
      apiClient.get('/students', { params: { page: 1, limit: 1000 } }),
      apiClient.get('/teachers', { params: { page: 1, limit: 1000 } }),
    ]);
    const students = studentsResult.status === 'fulfilled'
      ? getResponseArray(studentsResult.value) : [];
    const teachers = teachersResult.status === 'fulfilled'
      ? getResponseArray(teachersResult.value) : [];
    return [
      ...students.map((item, index) => `admin-enrollment-${item.id || index}`),
      ...teachers.map((item, index) => `admin-teacher-${item.id || index}`),
    ];
  }

  let students = [];
  if (role === 'parent') {
    const embeddedChildren = user?.children || user?.parent?.children;
    if (Array.isArray(embeddedChildren) && embeddedChildren.length) {
      students = embeddedChildren;
    } else {
      let childrenResponse;
      try {
        childrenResponse = await apiClient.get('/parents/me/children');
      } catch {
        childrenResponse = await apiClient.get('/parents/children');
      }
      students = getResponseArray(childrenResponse);
    }
  } else if (role === 'student') {
    const profileResponse = await apiClient.get(`/students/by-user/${user.id}`);
    const student = profileResponse?.data?.data;
    if (student) students = [student];
  }

  const activityGroups = await Promise.all(students.map(async (student) => {
    const [gradesResult, attendanceResult, subjectsResult] = await Promise.allSettled([
      apiClient.get(`/students/${student.id}/grades`),
      apiClient.get(`/students/${student.id}/attendance`),
      apiClient.get(`/students/${student.id}/subjects`),
    ]);
    const grades = gradesResult.status === 'fulfilled'
      ? getResponseArray(gradesResult.value) : [];
    const attendance = attendanceResult.status === 'fulfilled'
      ? getResponseArray(attendanceResult.value) : [];
    const subjects = subjectsResult.status === 'fulfilled'
      ? getResponseArray(subjectsResult.value) : [];
    const ids = [];

    grades.forEach((grade, gradeIndex) => {
      const subjectId = grade.subject_id || grade.subjectId || gradeIndex;
      const terms = grade.terms || {};
      ['term_1', 'term_2', 'term_3'].forEach((termKey) => {
        getRecordedAssessmentKeys(terms[termKey]).forEach((assessmentKey) => {
          ids.push(`grade-${subjectId}-${termKey}-${assessmentKey}`);
        });
      });
    });
    attendance.forEach((item) => ids.push(`attendance-${item.id}`));
    if (role === 'student') {
      subjects.forEach((item, index) => ids.push(`subject-${item.id || index}`));
    }
    if (student.enrollment_date || student.created_at || student.createdAt) {
      ids.push(`enrollment-${student.id}`);
    }
    return ids;
  }));

  return activityGroups.flat();
};

const buildImageUrl = (imagePath) => {
  if (!imagePath) {
    return '';
  }

  if (
    imagePath.startsWith('http://') ||
    imagePath.startsWith('https://')
  ) {
    return imagePath;
  }

  const normalizedPath = imagePath.startsWith('/')
    ? imagePath
    : `/${imagePath}`;

  return `${STATIC_BASE_URL}${normalizedPath}`;
};

// ========================================================================
// NAVIGATION DEFINITION
// Navigation entries are displayed according to the authenticated user role.
// Settings remains the final navigation item.
// ========================================================================
const navigationDefinitions = [
  {
    name: 'Dashboard',
    href: '/app/dashboard',
    icon: HomeIcon,
    roles: [
      'admin',
      'super_admin',
      'teacher',
      'student',
      'parent',
    ],
  },
  {
    name: 'Students',
    href: '/app/students',
    icon: UsersIcon,
    roles: ['admin', 'super_admin'],
  },
  {
    name: 'Teachers',
    href: '/app/teachers',
    icon: AcademicCapIcon,
    roles: ['admin', 'super_admin'],
  },
  {
    name: 'Classes',
    href: '/app/classes',
    icon: RectangleGroupIcon,
    roles: ['admin', 'super_admin', 'teacher'],
  },
  {
    name: 'Subjects',
    href: '/app/subjects',
    icon: PencilSquareIcon,
    roles: ['admin', 'super_admin'],
  },
  {
    name: 'Attendance',
    href: '/app/attendance',
    icon: CalendarDaysIcon,
    roles: ['super_admin', 'teacher', 'parent'],
  },

  // Grades is available only to super admins, students and parents.
  // Teachers use the class-specific teaching pages instead.
  {
    name: 'Grades',
    href: '/app/grades',
    icon: ChartBarIcon,
    roles: ['super_admin', 'student', 'parent'],
  },
  {
    name: 'Timetable',
    href: '/app/timetable',
    icon: ClockIcon,
    roles: ['admin', 'super_admin', 'teacher', 'student'],
  },
  {
    name: 'Reports',
    href: '/app/reports',
    icon: DocumentTextIcon,
    roles: ['super_admin'],
  },
  {
    name: 'Finance',
    href: '/app/finance',
    icon: BanknotesIcon,
    roles: ['admin', 'school_admin'],
  },

  // Teacher navigation
  {
    name: 'Students',
    href: '/app/students',
    icon: UserGroupIcon,
    roles: ['teacher'],
  },
  // Student navigation
  {
    name: 'Subjects',
    href: '/app/subjects',
    icon: PencilSquareIcon,
    roles: ['student'],
  },
  {
    name: 'Library',
    href: '/app/library',
    icon: FolderIcon,
    roles: ['student', 'teacher'],
  },

  // Parent navigation
  {
    name: 'My Children',
    href: '/app/my-children',
    icon: UserCircleIcon,
    roles: ['parent'],
  },
  {
    name: "Child's Grades",
    href: '/app/child-grades',
    icon: ChartBarIcon,
    roles: ['parent'],
  },
  {
    name: "Child's Attendance",
    href: '/app/child-attendance',
    icon: CalendarDaysIcon,
    roles: ['parent'],
  },

  // Settings must remain last.
  {
    name: 'Settings',
    href: '/app/settings',
    icon: Cog6ToothIcon,
    roles: [
      'admin',
      'super_admin',
      'student',
      'teacher',
      'parent',
    ],
  },
];

// Mobile bottom navigation items.
// Settings is excluded because it is a secondary action.
const bottomNavMap = {
  admin: [
    'Dashboard',
    'Students',
    'Teachers',
    'Subjects',
    'Classes',
  ],
  super_admin: [
    'Dashboard',
    'Students',
    'Teachers',
    'Subjects',
    'Classes',
  ],
  teacher: [
    'Dashboard',
    'Classes',
    'Library',
    'Timetable',
    'Attendance',
  ],
  student: [
    'Dashboard',
    'Subjects',
    'Grades',
    'Library',
    'Timetable',
  ],
  parent: [
    'Dashboard',
    'My Children',
    "Child's Grades",
    "Child's Attendance",
  ],
};

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState('');
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const [assignedTeacherClassId, setAssignedTeacherClassId] = useState(null);
  const [notificationActivityIds, setNotificationActivityIds] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    logout,
    loading,
  } = useAuth();

  // Normalize the role to lowercase for consistent matching.
  const rawRole = user?.role;
  const role = rawRole ? rawRole.toLowerCase() : '';
  const isAdmin = ADMIN_ROLES.includes(role);
  const accountIdentity = user?.uuid || user?.id || user?.email;
  const notificationReadStorageKey = accountIdentity
    ? `pawaschool-${role || 'user'}-activity-read:${accountIdentity}`
    : null;

  useEffect(() => {
    let isMounted = true;

    const loadNotificationIds = async () => {
      if (!user?.id || !role) {
        if (isMounted) setNotificationActivityIds([]);
        return;
      }
      try {
        const ids = await fetchNotificationActivityIds(user, role);
        if (isMounted) setNotificationActivityIds([...new Set(ids)]);
      } catch (error) {
        if (isMounted) setNotificationActivityIds([]);
        console.error('Failed to load notification count:', error);
      }
    };

    loadNotificationIds();
    return () => { isMounted = false; };
  }, [user, role]);

  useEffect(() => {
    const updateUnreadCount = () => {
      if (!notificationReadStorageKey) {
        setUnreadNotificationCount(0);
        return;
      }
      try {
        const saved = JSON.parse(
          localStorage.getItem(notificationReadStorageKey) || '[]'
        );
        const readIds = new Set(Array.isArray(saved) ? saved : []);
        setUnreadNotificationCount(
          notificationActivityIds.filter((id) => !readIds.has(id)).length
        );
      } catch {
        setUnreadNotificationCount(notificationActivityIds.length);
      }
    };

    updateUnreadCount();
    window.addEventListener('storage', updateUnreadCount);
    window.addEventListener('pawaschool-notifications-updated', updateUnreadCount);
    return () => {
      window.removeEventListener('storage', updateUnreadCount);
      window.removeEventListener('pawaschool-notifications-updated', updateUnreadCount);
    };
  }, [notificationActivityIds, notificationReadStorageKey, location.pathname]);

  const personalProfile =
    user?.student ||
    user?.teacher ||
    user?.parent ||
    user?.profile ||
    {};
  const firstName =
    personalProfile.first_name ||
    personalProfile.firstName ||
    user?.first_name ||
    user?.firstName ||
    '';
  const lastName =
    personalProfile.last_name ||
    personalProfile.lastName ||
    user?.last_name ||
    user?.lastName ||
    '';
  const profileName =
    personalProfile.full_name ||
    personalProfile.fullName ||
    user?.full_name ||
    user?.fullName ||
    `${firstName} ${lastName}`.trim() ||
    user?.name ||
    'User';
  const profileInitials =
    profileName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'U';
  const personalPhoto =
    personalProfile.profile_picture ||
    personalProfile.profilePicture ||
    personalProfile.photo_url ||
    personalProfile.photoUrl ||
    user?.profile_picture ||
    user?.profilePicture ||
    user?.photo_url ||
    user?.photoUrl ||
    user?.avatar ||
    '';
  const personalPhotoUrl = buildImageUrl(personalPhoto);

  // The backend may return the teacher's class assignment in slightly
  // different shapes. Resolve the first available class ID without ever
  // hard-coding a test class such as 96.
  const teacherClassId = useMemo(() => {
    if (role !== 'teacher') {
      return null;
    }

    const teacher = user?.teacher || user?.Teacher || {};
    const assignments =
      user?.teacher_classes ||
      user?.teacherClasses ||
      user?.TeacherClasses ||
      teacher?.teacher_classes ||
      teacher?.teacherClasses ||
      teacher?.TeacherClasses ||
      [];

    const firstAssignment = Array.isArray(assignments)
      ? assignments[0] || {}
      : assignments || {};

    const assignedClass =
      firstAssignment?.class ||
      firstAssignment?.Class ||
      user?.class ||
      user?.Class ||
      teacher?.class ||
      teacher?.Class ||
      {};

    return (
      user?.class_id ||
      user?.classId ||
      teacher?.class_id ||
      teacher?.classId ||
      firstAssignment?.class_id ||
      firstAssignment?.classId ||
      assignedClass?.id ||
      null
    );
  }, [role, user]);

  // The Classes endpoint already returns only the classes available to a
  // teacher. Fetch it here so navigation uses the same real class ID as the
  // "View Attendance" button on the Class Details page.
  useEffect(() => {
    let isMounted = true;

    const fetchTeacherClass = async () => {
      if (role !== 'teacher') {
        if (isMounted) {
          setAssignedTeacherClassId(null);
        }
        return;
      }

      // Avoid an unnecessary request when the authenticated user already
      // contains the assigned class ID.
      if (teacherClassId) {
        if (isMounted) {
          setAssignedTeacherClassId(teacherClassId);
        }
        return;
      }

      try {
        const response = await apiClient.get('/classes', {
          params: {
            page: 1,
            limit: 100,
          },
        });

        const responseData = response?.data?.data ?? response?.data ?? [];
        const classes = Array.isArray(responseData)
          ? responseData
          : responseData?.classes || responseData?.rows || [];

        const firstAssignedClass = Array.isArray(classes)
          ? classes.find((classItem) => classItem?.id)
          : null;

        if (isMounted) {
          setAssignedTeacherClassId(firstAssignedClass?.id || null);
        }
      } catch (error) {
        if (isMounted) {
          setAssignedTeacherClassId(null);
          console.error('Failed to fetch teacher class:', error);
        }
      }
    };

    fetchTeacherClass();

    return () => {
      isMounted = false;
    };
  }, [role, teacherClassId]);

  const resolvedTeacherClassId =
    teacherClassId || assignedTeacherClassId;

  const teacherAttendanceHref = resolvedTeacherClassId
    ? `/app/classes/${resolvedTeacherClassId}/attendance`
    : '/app/classes';

  useEffect(() => {
    let isMounted = true;

    const fetchSchool = async () => {
      // Only administrators use the school logo in the top navigation.
      if (!isAdmin) {
        if (isMounted) setSchoolLogo('');
        return;
      }

      try {
        const response = await getSchoolProfile();

        if (!isMounted || !response?.success) {
          return;
        }

        const school = response.data || {};
        const rawLogo =
          school.logo_url ||
          school.logoUrl ||
          '';

        setSchoolLogo(buildImageUrl(rawLogo));
      } catch (error) {
        if (isMounted) {
          console.error(
            'Failed to fetch school profile:',
            error
          );
        }
      }
    };

    fetchSchool();

    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  useEffect(() => {
    setProfileImageFailed(false);
  }, [personalPhotoUrl, user?.id]);

  // ================================================================
  // FIXED LOGOUT HANDLER
  // Forces navigation to /login even if the context logout redirects
  // to /super-admin/login (which no longer exists).
  // ================================================================
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Override any redirect performed by the logout function.
      navigate('/login', {
        replace: true,
      });
    }
  };

  // Filter the navigation entries according to the current role.
  const navigation = useMemo(() => {
    if (!role) {
      return [];
    }

    return navigationDefinitions
      .filter((item) =>
        item.roles.some(
          (allowedRole) =>
            allowedRole.toLowerCase() === role
        )
      )
      .map((item) => {
        if (role === 'teacher' && item.name === 'Attendance') {
          return {
            ...item,
            href: teacherAttendanceHref,
          };
        }

        return item;
      });
  }, [role, teacherAttendanceHref]);

  const bottomNavItems = useMemo(() => {
    const itemNames = bottomNavMap[role] || [];

    const navigationMap = new Map(
      navigation.map((item) => [item.name, item])
    );

    return itemNames
      .map((name) => navigationMap.get(name))
      .filter(Boolean);
  }, [navigation, role]);

  const isActive = (href) => {
    return (
      location.pathname === href ||
      location.pathname.startsWith(`${href}/`)
    );
  };

  if (loading) {
    return <PageLoader />;
  }

  if (!role) {
    return (
      <div className="p-8 text-center text-red-600">
        Unable to determine your user role. Please log in
        again.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          sidebarOpen ? 'block' : 'hidden'
        }`}
      >
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />

        <aside className="fixed inset-y-0 left-0 flex w-64 flex-col border-r border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
            <span className="text-xl font-bold text-primary-600">
              PawaSchool
            </span>

            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
              className="rounded-lg p-1 transition-colors hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6 text-primary-600" />
            </button>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto p-3">
            {navigation.map((item) => {
              const active = isActive(item.href);
              const ItemIcon = item.icon;

              return (
                <Link
                  key={`${item.name}-${item.href}`}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center rounded-md px-3 py-2.5 text-sm transition-colors ${
                    active
                      ? 'bg-primary-50 font-semibold text-primary-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ItemIcon className="mr-3 h-5 w-5" />

                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-gray-200 p-3">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="truncate font-semibold text-gray-700">
                  {user?.first_name ||
                    user?.name ||
                    'User'}
                </p>

                <p className="text-xs capitalize text-gray-500">
                  {role.replace('_', ' ')}
                </p>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                aria-label="Log out"
                className="rounded-lg p-2 transition-colors hover:bg-red-50"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 text-red-500" />
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-60">
        <div className="flex flex-1 flex-col border-r border-gray-200 bg-white">
          <div className="flex items-center border-b border-gray-200 px-4 py-4">
            <span className="text-lg font-bold text-primary-600">
              PawaSchool
            </span>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto p-3">
            {navigation.map((item) => {
              const active = isActive(item.href);
              const ItemIcon = item.icon;

              return (
                <Link
                  key={`${item.name}-${item.href}`}
                  to={item.href}
                  className={`flex items-center rounded-md px-3 py-2.5 text-sm transition-colors ${
                    active
                      ? 'bg-primary-50 font-semibold text-primary-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ItemIcon className="mr-3 h-[18px] w-[18px] flex-shrink-0" />

                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-gray-200 p-3">
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 text-red-500" />

              <span className="font-medium">
                Logout
              </span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-60">
        <header className="sticky top-0 z-40 border-b border-gray-200 bg-white">
          <div className="flex min-h-16 items-center justify-between px-4 py-3 lg:px-6">
            {/* Mobile menu button */}
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 lg:hidden"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="ml-auto flex items-center space-x-4">
              <button
                type="button"
                onClick={() =>
                  navigate('/app/notifications')
                }
                className="relative flex h-10 w-10 touch-manipulation items-center justify-center rounded-full transition-colors hover:bg-primary-50"
                aria-label={`Notifications${unreadNotificationCount ? `, ${unreadNotificationCount} unread` : ''}`}
              >
                <BellIcon className="h-6 w-6 text-gray-600 hover:text-gray-700" />
                {unreadNotificationCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex min-h-[20px] min-w-[20px] items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm">
                    {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setIsDrawerOpen(true)}
                className="flex h-9 w-9 touch-manipulation items-center justify-center overflow-hidden rounded-full border border-white bg-primary-600 transition-colors hover:border-primary-300"
                aria-label={isAdmin ? 'Open school profile' : 'Open my profile'}
              >
                {isAdmin && schoolLogo ? (
                  <img
                    src={schoolLogo}
                    alt="School logo"
                    className="h-full w-full object-cover"
                    onError={() => setSchoolLogo('')}
                  />
                ) : !isAdmin && personalPhotoUrl && !profileImageFailed ? (
                  <img
                    src={personalPhotoUrl}
                    alt={`${profileName} profile`}
                    className="h-full w-full object-cover"
                    onError={() => setProfileImageFailed(true)}
                  />
                ) : (
                  <span className="text-sm font-semibold text-white">
                    {profileInitials}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        <main className="p-4 pb-20 lg:p-8 lg:pb-0">
          <Outlet />
        </main>

        {/* Mobile bottom navigation */}
        <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white lg:hidden">
          <div className="flex h-16 items-stretch justify-around px-1">
            {bottomNavItems.map((item) => {
              const active = isActive(item.href);
              const ItemIcon = item.icon;

              return (
                <Link
                  key={`${item.name}-${item.href}`}
                  to={item.href}
                  className={`flex flex-1 flex-col items-center justify-center px-0.5 py-1 transition-colors ${
                    active
                      ? 'text-primary-600'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <ItemIcon className="h-6 w-6" />

                  <span className="mt-0.5 max-w-[64px] break-words text-center text-[10px] font-medium leading-tight sm:text-xs">
                    {item.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>

      <ProfileDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onUpdate={(updatedSchool) => {
          if (!isAdmin) return;

          const rawLogo =
            updatedSchool?.logo_url ||
            updatedSchool?.logoUrl ||
            '';

          setSchoolLogo(buildImageUrl(rawLogo));
        }}
      />
    </div>
  );
}